import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ALL_CURRENCIES, ON_CURRENCY_CHANGED } from '../../graphql/currencies';
import CurrenciesList from './CurrenciesList';
import Paginator from '../Paginator';
import {
    parseIncludeDeactivated,
    parsePaging,
    parseSorting,
} from '../../utils/queryString';
import CreateCurrencyRow from './CreateCurrencyRow';
import { showInfoNotification } from '../../actions/notifications';
import Spinner from '../Spinner';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import SubscriptionActions from '../../utils/SubscriptionActions';
import UploadButton from '../buttons/UploadButton';
import Constants from '../../utils/Constants';
import Error from '../errors/Error';
import CheckboxField from '../fields/CheckboxField';
import { getValueMapping } from './CurrencyRow';
import DownloadSampleFile from '../DownloadSampleFile';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';

export function createNotificationText(t, action, name, code) {
    let notification;
    switch (action) {
        case SubscriptionActions.CURRENCY_CREATED:
            notification = t('currencies.notification.currency-create-thirdparty', { name, code });
            break;
        case SubscriptionActions.CURRENCY_UPDATED:
            notification = t('currencies.notification.currency-update-thirdparty', { name, code });
            break;
        default:
            notification = t('currencies.notification.currencies-modified-thirdparty');
    }
    return notification;
}

function Currencies() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'code',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const includeDeactivated = parseIncludeDeactivated(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        sortKey,
        sortDirection,
        includeDeactivated,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_CURRENCIES, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onUpload = () => refetch(refetchProps);
    const onFilter = (newIncludeDeactivated) => setRefetchProps({
        ...refetchProps,
        includeDeactivated: newIncludeDeactivated,
        page: 0,
    });
    useExtendedSubscription(ON_CURRENCY_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'currencyChanged',
        onChangeCallback: ({ action, model: { models, totalCount } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.CURRENCY_EDIT])) {
                if (totalCount === 1) {
                    const { name, code } = models[0];
                    const notification = createNotificationText(t, action, name, code);
                    dispatch(showInfoNotification(notification));
                } else {
                    const notification = t('currencies.notification.currencies-modified-thirdparty');
                    dispatch(showInfoNotification(notification));
                }
            }
        },
    });
    let result;
    if (loading) {
        result = <Spinner className="currencies-loading" />;
    } else if (error) {
        result = <Error className="currencies-error" error={error} />;
    } else {
        result = (
            <Paginator
                totalCount={data.allCurrencies.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <CurrenciesList
                    currencies={data.allCurrencies.models}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="globe" />&nbsp;{t('title.currencies')}</h4>
            <CreateCurrencyRow />
            <div className="search-and-filter">
                <UploadButton
                    label={t('currencies.upload-exchange-rates')}
                    className="button-bordered"
                    uploadPath={Constants.UPLOAD_EXCHANGE_RATES_PATH}
                    accept=".csv"
                    onUpload={onUpload}
                >
                    <DownloadSampleFile
                        text={t('currencies.download-sample-file-text')}
                        fileName="sampleExchangeRateUploadFile"
                        sampleType="EXCHANGE_RATE"
                        fileTypes={['CSV']}
                    />
                </UploadButton>
                <div className="filter">
                    <CheckboxField
                        label={t('currencies.include-deactivated')}
                        value={refetchProps.includeDeactivated}
                        onChange={onFilter}
                        className="currencies--search-and-filter--include-deactivated"
                        inputId="currencies--search-and-filter--include-deactivated"
                    />
                </div>
            </div>
            {result}
        </>
    );
}

export default Currencies;
